import React, { useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';

// Simplified constellation background for legal pages
const ConstellationBackground = ({ theme }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const nodesRef = useRef([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let width = window.innerWidth;
    let height = document.documentElement.scrollHeight;

    const resize = () => {
      width = window.innerWidth;
      height = Math.max(document.documentElement.scrollHeight, window.innerHeight);
      canvas.width = width;
      canvas.height = height;
      initNodes();
    };

    const initNodes = () => {
      const nodeCount = Math.floor((width * height) / 25000);
      nodesRef.current = [];
      for (let i = 0; i < nodeCount; i++) {
        nodesRef.current.push({
          x: Math.random() * width,
          y: Math.random() * height,
          vx: (Math.random() - 0.5) * 0.15,
          vy: (Math.random() - 0.5) * 0.15,
          radius: Math.random() * 1.5 + 0.5,
          pulse: Math.random() * Math.PI * 2,
        });
      }
    };

    const draw = () => {
      const isDark = theme === 'dark';
      ctx.fillStyle = isDark
        ? 'rgba(10, 22, 40, 0.03)'
        : 'rgba(239, 246, 255, 0.03)';
      ctx.fillRect(0, 0, width, height);

      const nodeColor = isDark ? 'rgba(147, 197, 253, ' : 'rgba(59, 130, 246, ';
      const lineColor = isDark ? 'rgba(147, 197, 253, ' : 'rgba(59, 130, 246, ';

      nodesRef.current.forEach((node, i) => {
        node.x += node.vx;
        node.y += node.vy;
        node.pulse += 0.01;

        if (node.x < 0 || node.x > width) node.vx *= -1;
        if (node.y < 0 || node.y > height) node.vy *= -1;

        const pulseScale = 1 + Math.sin(node.pulse) * 0.2;
        const radius = node.radius * pulseScale;

        ctx.beginPath();
        ctx.arc(node.x, node.y, radius, 0, Math.PI * 2);
        ctx.fillStyle = nodeColor + (0.3 + Math.sin(node.pulse) * 0.1) + ')';
        ctx.fill();

        nodesRef.current.slice(i + 1).forEach(other => {
          const dx = other.x - node.x;
          const dy = other.y - node.y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < 120) {
            ctx.beginPath();
            ctx.moveTo(node.x, node.y);
            ctx.lineTo(other.x, other.y);
            ctx.strokeStyle = lineColor + (0.08 * (1 - dist / 120)) + ')';
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        });
      });

      animationRef.current = requestAnimationFrame(draw);
    };

    resize();
    window.addEventListener('resize', resize);
    draw();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationRef.current);
    };
  }, [theme]);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none"
      style={{ zIndex: 0 }}
    />
  );
};

// Section component with staggered animation
const Section = ({ number, title, children, delay = 0 }) => (
  <section
    className="mb-10 animate-fade-in"
    style={{ animationDelay: `${delay}ms` }}
  >
    <div className="flex items-start gap-4 mb-4">
      <span className="flex-shrink-0 w-10 h-10 rounded-xl bg-alpha-gradient flex items-center justify-center text-white font-bold text-sm shadow-alpha">
        {number}
      </span>
      <h2 className="text-2xl font-bold text-silver-800 dark:text-white pt-1">
        {title}
      </h2>
    </div>
    <div className="pl-14 text-silver-600 dark:text-silver-400 leading-relaxed space-y-4">
      {children}
    </div>
  </section>
);

// Subsection component
const Subsection = ({ title, children }) => (
  <div className="mt-6">
    <h3 className="text-lg font-semibold text-silver-700 dark:text-silver-300 mb-3">
      {title}
    </h3>
    <div className="space-y-3">
      {children}
    </div>
  </div>
);

// List item with icon
const ListItem = ({ children, type = 'check' }) => (
  <li className="flex items-start gap-3">
    <span className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5 ${
      type === 'check'
        ? 'bg-alpha-100 dark:bg-alpha-900/50'
        : 'bg-silver-100 dark:bg-silver-800/50'
    }`}>
      {type === 'check' ? (
        <svg className="w-3 h-3 text-alpha-600 dark:text-alpha-400" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
      ) : (
        <span className="w-1.5 h-1.5 rounded-full bg-silver-400 dark:bg-silver-500" />
      )}
    </span>
    <span>{children}</span>
  </li>
);

const PrivacyPolicy = ({ theme }) => {
  const navigate = useNavigate();

  return (
    <div className={`relative min-h-screen ${theme === 'dark' ? 'dark' : ''}`}>
      {/* Background */}
      <div className="fixed inset-0 bg-gradient-to-br from-alpha-50 via-white to-silver-100 dark:from-navy-950 dark:via-navy-900 dark:to-alpha-950 transition-colors duration-500" />
      <ConstellationBackground theme={theme} />

      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/80 dark:bg-navy-900/80 backdrop-blur-xl border-b border-silver-200/50 dark:border-silver-700/20">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-silver-600 dark:text-silver-400 hover:text-alpha-500 dark:hover:text-alpha-400 transition-colors font-medium"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>

          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="HeyAlphas" className="w-8 h-8" />
            <span className="text-xl font-black tracking-tight text-silver-800 dark:text-white">
              Hey<span className="text-alpha-500">Alphas</span>
            </span>
          </Link>
        </div>
      </nav>

      {/* Main Content */}
      <main className="relative z-10 max-w-4xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-12 animate-slide-up">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-alpha-gradient shadow-alpha-lg mb-6">
            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-silver-800 dark:text-white mb-3">
            Privacy <span className="text-alpha-500">Policy</span>
          </h1>
          <p className="text-silver-500 dark:text-silver-400">
            Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        </div>

        {/* Content Card */}
        <div className="relative bg-white/70 dark:bg-navy-800/50 backdrop-blur-xl rounded-3xl border border-silver-200/50 dark:border-silver-700/20 p-8 md:p-12 shadow-xl">
          {/* Decorative corner accents */}
          <div className="absolute top-6 right-6 w-12 h-12 border-t-2 border-r-2 border-alpha-300/30 dark:border-alpha-500/20 rounded-tr-2xl" />
          <div className="absolute bottom-6 left-6 w-12 h-12 border-b-2 border-l-2 border-alpha-300/30 dark:border-alpha-500/20 rounded-bl-2xl" />

          <Section number="1" title="Introduction" delay={100}>
            <p>
              HeyAlphas ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains
              how we collect, use, disclose, and safeguard your information when you use our web application and services.
              This policy applies to all users of HeyAlphas, whether accessed through our website or mobile applications.
            </p>
            <p>
              By using our Service, you consent to the collection and use of information in accordance with this policy.
              If you do not agree with our policies and practices, please do not use our Service.
            </p>
          </Section>

          <Section number="2" title="Information We Collect" delay={150}>
            <Subsection title="2.1 Personal Information">
              <p>We collect personal information that you provide directly to us, including:</p>
              <ul className="list-none space-y-2">
                <ListItem><strong>Account Information:</strong> When you create an account through Google OAuth or Apple Sign-in, we collect your email address, name, and profile picture (if provided)</ListItem>
                <ListItem><strong>Payment Information:</strong> When you subscribe to premium features, payment information is processed by Stripe or Apple. We do not store your complete payment card information</ListItem>
                <ListItem><strong>Communication Data:</strong> Any information you provide when contacting our support team</ListItem>
              </ul>
            </Subsection>

            <Subsection title="2.2 Usage Information">
              <p>We automatically collect certain information about your use of our Service:</p>
              <ul className="list-none space-y-2">
                <ListItem><strong>Watchlist Data:</strong> Cryptocurrency tokens you add to your watchlist, including token addresses, names, and timestamps</ListItem>
                <ListItem><strong>Search History:</strong> Search queries you perform within the application</ListItem>
                <ListItem><strong>Usage Patterns:</strong> Pages visited, features used, time spent on different sections, and user preferences</ListItem>
                <ListItem><strong>Session Data:</strong> Authentication tokens and session information stored locally in your browser</ListItem>
              </ul>
            </Subsection>

            <Subsection title="2.3 Technical Information">
              <p>We collect technical data through Google Analytics and our application:</p>
              <ul className="list-none space-y-2">
                <ListItem><strong>Device Information:</strong> Browser type, operating system, screen resolution, and device identifiers</ListItem>
                <ListItem><strong>Log Data:</strong> IP address, access times, pages viewed, and referring pages</ListItem>
                <ListItem><strong>Analytics Data:</strong> User interactions, feature usage, and performance metrics</ListItem>
                <ListItem><strong>Location Data:</strong> Approximate geographic location based on IP address (country/region level)</ListItem>
              </ul>
            </Subsection>
          </Section>

          <Section number="3" title="How We Use Your Information" delay={200}>
            <p>We use the information we collect for the following purposes:</p>
            <ul className="list-none space-y-2">
              <ListItem><strong>Service Provision:</strong> To provide, maintain, and improve our cryptocurrency tracking and analytics services</ListItem>
              <ListItem><strong>Account Management:</strong> To create and manage your account, authenticate users, and enable personalized features</ListItem>
              <ListItem><strong>Premium Features:</strong> To process subscriptions and provide an ad-free experience with priority support</ListItem>
              <ListItem><strong>Personalization:</strong> To customize your experience, save your preferences, and provide relevant content</ListItem>
              <ListItem><strong>Analytics:</strong> To analyze usage patterns, improve our Service, and develop new features</ListItem>
              <ListItem><strong>Communication:</strong> To send you service-related notifications, updates, and customer support</ListItem>
              <ListItem><strong>Security:</strong> To protect against fraud, abuse, and security threats</ListItem>
              <ListItem><strong>Legal Compliance:</strong> To comply with applicable laws, regulations, and legal requests</ListItem>
            </ul>
          </Section>

          <Section number="4" title="Information Sharing and Disclosure" delay={250}>
            <Subsection title="4.1 Third-Party Service Providers">
              <ul className="list-none space-y-2">
                <ListItem><strong>Google:</strong> For OAuth authentication services and Google Analytics</ListItem>
                <ListItem><strong>Apple:</strong> For Apple Sign-in authentication and payment processing (for mobile app)</ListItem>
                <ListItem><strong>Stripe:</strong> For payment processing and subscription management</ListItem>
                <ListItem><strong>Cloud Service Providers:</strong> For hosting, data storage, and application infrastructure</ListItem>
              </ul>
            </Subsection>

            <Subsection title="4.2 Legal Requirements">
              <p>We may disclose your information when required to:</p>
              <ul className="list-none space-y-2">
                <ListItem type="bullet">Comply with applicable laws, regulations, or legal processes</ListItem>
                <ListItem type="bullet">Respond to lawful requests from public authorities</ListItem>
                <ListItem type="bullet">Protect our rights, property, or safety, or that of our users</ListItem>
                <ListItem type="bullet">Investigate potential fraud or security issues</ListItem>
              </ul>
            </Subsection>

            <Subsection title="4.3 Business Transfers">
              <p>
                In the event of a merger, acquisition, or sale of assets, your information may be transferred to the new entity,
                subject to the same privacy protections outlined in this policy.
              </p>
            </Subsection>
          </Section>

          <Section number="5" title="Data Security" delay={300}>
            <p>
              We implement appropriate technical and organizational security measures to protect your personal information against
              unauthorized access, alteration, disclosure, or destruction. These measures include:
            </p>
            <ul className="list-none space-y-2">
              <ListItem>Encryption of data in transit and at rest</ListItem>
              <ListItem>Regular security assessments and updates</ListItem>
              <ListItem>Access controls and authentication requirements</ListItem>
              <ListItem>Secure hosting and infrastructure partners</ListItem>
              <ListItem>Regular monitoring for security threats</ListItem>
            </ul>
            <div className="bg-silver-100/50 dark:bg-navy-700/30 rounded-2xl p-5 border border-silver-200/50 dark:border-silver-700/30 mt-4">
              <p className="text-sm">
                However, no method of transmission over the internet or electronic storage is 100% secure. We cannot guarantee
                absolute security but strive to use commercially acceptable means to protect your personal information.
              </p>
            </div>
          </Section>

          <Section number="6" title="Data Retention" delay={350}>
            <p>
              We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy. Specifically:
            </p>
            <ul className="list-none space-y-2">
              <ListItem><strong>Account Data:</strong> Retained while your account is active and for a reasonable period after account deletion</ListItem>
              <ListItem><strong>Watchlist Data:</strong> Retained while your account is active or until you remove items from your watchlist</ListItem>
              <ListItem><strong>Usage Data:</strong> Typically retained for 2-3 years for analytics and improvement purposes</ListItem>
              <ListItem><strong>Payment Data:</strong> Retained according to Stripe's and Apple's retention policies for billing and compliance</ListItem>
              <ListItem><strong>Legal Compliance:</strong> Some data may be retained longer when required by law</ListItem>
            </ul>
          </Section>

          <Section number="7" title="Your Privacy Rights" delay={400}>
            <Subsection title="7.1 General Rights">
              <ul className="list-none space-y-2">
                <ListItem><strong>Access:</strong> Request access to your personal information</ListItem>
                <ListItem><strong>Correction:</strong> Request correction of inaccurate or incomplete data</ListItem>
                <ListItem><strong>Deletion:</strong> Request deletion of your personal information</ListItem>
                <ListItem><strong>Portability:</strong> Request a copy of your data in a portable format</ListItem>
                <ListItem><strong>Objection:</strong> Object to certain processing of your personal information</ListItem>
              </ul>
            </Subsection>

            <Subsection title="7.2 Account Controls">
              <ul className="list-none space-y-2">
                <ListItem type="bullet">Update your profile information through your account settings</ListItem>
                <ListItem type="bullet">Manage your watchlist by adding or removing items</ListItem>
                <ListItem type="bullet">Cancel your premium subscription at any time</ListItem>
                <ListItem type="bullet">Delete your account through account settings or by contacting support</ListItem>
                <ListItem type="bullet">Opt out of promotional communications</ListItem>
              </ul>
            </Subsection>

            <Subsection title="7.3 California Residents (CCPA)">
              <p>
                California residents have additional rights under the California Consumer Privacy Act, including the right to
                know what personal information is collected, the right to delete personal information, and the right to opt-out
                of the sale of personal information. We do not sell personal information.
              </p>
            </Subsection>

            <Subsection title="7.4 European Union Residents (GDPR)">
              <p>
                EU residents have rights under the General Data Protection Regulation, including the right to access, rectify,
                erase, restrict processing, data portability, and to object to processing. You also have the right to lodge
                a complaint with a supervisory authority.
              </p>
            </Subsection>
          </Section>

          <Section number="8" title="Children's Privacy" delay={450}>
            <p>
              Our Service is not intended for children under the age of 13 (or 16 in the European Union). We do not knowingly
              collect personal information from children under these ages. If we become aware that we have collected personal
              information from a child under the applicable age limit, we will take steps to delete such information.
            </p>
            <p>
              If you are a parent or guardian and believe your child has provided us with personal information, please contact
              us so we can delete the child's information.
            </p>
          </Section>

          <Section number="9" title="International Data Transfers" delay={500}>
            <p>
              Your information may be transferred to, processed, and stored in countries other than your country of residence.
              These countries may have data protection laws that differ from those in your country.
            </p>
            <p>
              We ensure that such transfers are made in accordance with applicable laws and that appropriate safeguards are in place
              to protect your personal information, including:
            </p>
            <ul className="list-none space-y-2">
              <ListItem type="bullet">Standard contractual clauses approved by relevant authorities</ListItem>
              <ListItem type="bullet">Adequacy decisions by competent authorities</ListItem>
              <ListItem type="bullet">Other legally recognized transfer mechanisms</ListItem>
            </ul>
          </Section>

          <Section number="10" title="Third-Party Links and Services" delay={550}>
            <p>
              Our Service may contain links to third-party websites, applications, or services that are not controlled by us.
              We are not responsible for the privacy practices of these third parties.
            </p>
            <p>Key third-party services we use include:</p>
            <ul className="list-none space-y-2">
              <ListItem><strong>Google Analytics:</strong> For website analytics (subject to Google's Privacy Policy)</ListItem>
              <ListItem><strong>Google OAuth:</strong> For authentication (subject to Google's Privacy Policy)</ListItem>
              <ListItem><strong>Apple Sign-in:</strong> For authentication (subject to Apple's Privacy Policy)</ListItem>
              <ListItem><strong>Stripe:</strong> For payment processing (subject to Stripe's Privacy Policy)</ListItem>
            </ul>
          </Section>

          <Section number="11" title="Updates to This Privacy Policy" delay={600}>
            <p>
              We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal
              requirements, or other factors. We will notify you of material changes by:
            </p>
            <ul className="list-none space-y-2">
              <ListItem type="bullet">Updating the "Last updated" date at the top of this policy</ListItem>
              <ListItem type="bullet">Providing notice through our Service or via email</ListItem>
              <ListItem type="bullet">For material changes, we may require your consent</ListItem>
            </ul>
            <p>
              Your continued use of our Service after any changes constitutes your acceptance of the updated Privacy Policy.
            </p>
          </Section>

          <Section number="12" title="Contact Us" delay={650}>
            <p>
              If you have any questions, concerns, or requests regarding this Privacy Policy or our privacy practices,
              please contact us:
            </p>
            <div className="bg-alpha-50/50 dark:bg-alpha-900/20 rounded-2xl p-5 border border-alpha-200/50 dark:border-alpha-700/30">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 rounded-lg bg-alpha-gradient flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <span className="font-semibold text-silver-800 dark:text-white">Get in Touch</span>
              </div>
              <div className="space-y-1 text-sm">
                <p><strong>Privacy:</strong> <a href="mailto:privacy@woleh.com" className="text-alpha-600 dark:text-alpha-400 hover:underline">privacy@woleh.com</a></p>
                <p><strong>Support:</strong> <a href="mailto:support@woleh.com" className="text-alpha-600 dark:text-alpha-400 hover:underline">support@woleh.com</a></p>
                <p className="text-silver-500 dark:text-silver-400 mt-2">We aim to respond to privacy inquiries within 30 days</p>
              </div>
            </div>
            <p className="mt-4">
              For EU residents, you also have the right to contact your local data protection authority if you have concerns
              about how we handle your personal information.
            </p>
          </Section>

          <Section number="13" title="Legal Basis for Processing (EU Users)" delay={700}>
            <p>
              For users in the European Union, we process your personal information based on the following legal grounds:
            </p>
            <ul className="list-none space-y-2">
              <ListItem><strong>Contract Performance:</strong> To provide our services and fulfill our obligations to you</ListItem>
              <ListItem><strong>Legitimate Interests:</strong> To improve our services, prevent fraud, and ensure security</ListItem>
              <ListItem><strong>Consent:</strong> For marketing communications and certain optional features (you may withdraw consent at any time)</ListItem>
              <ListItem><strong>Legal Obligations:</strong> To comply with applicable laws and regulations</ListItem>
            </ul>
          </Section>
        </div>

        {/* Footer navigation */}
        <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4 text-sm text-silver-500 dark:text-silver-400">
          <Link to="/terms" className="hover:text-alpha-500 dark:hover:text-alpha-400 transition-colors">
            Terms of Service
          </Link>
          <span className="hidden sm:inline">•</span>
          <Link to="/support" className="hover:text-alpha-500 dark:hover:text-alpha-400 transition-colors">
            Support
          </Link>
          <span className="hidden sm:inline">•</span>
          <Link to="/" className="hover:text-alpha-500 dark:hover:text-alpha-400 transition-colors">
            Back to Home
          </Link>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
